/**
 * Scheme allow-listing for URLs that reach an href, window.open() or a
 * redirect.
 *
 * Any URL that originates outside this app — a tenant-configured link, a
 * third-party API payload — is untrusted at the point of render, regardless
 * of what validated it on the way in. Client-side form validation is advice
 * to the user, not a control: it is bypassed by a direct API call.
 *
 * `javascript:` is the case that matters. A `javascript:` URI in an href
 * executes with the page's origin when clicked, so an unsanitised
 * tenant-supplied link on a public page is stored XSS.
 */

// Schemes that are safe to navigate to. http is included so tenant values
// stored before https validation existed keep working; it is a transport
// weakness, not a script-execution vector. Everything else — javascript:,
// data:, vbscript:, blob:, file: — is rejected.
const ALLOWED_PROTOCOLS = ['https:', 'http:']

// C0 controls and space, DEL and C1, plus the Unicode separators a URL
// parser would otherwise tolerate mid-scheme. Escapes, not literals, so the
// source stays greppable and free of invisible characters.
const CONTROL_AND_SPACE = /[\u0000-\u0020\u007F-\u009F\u00A0\u1680\u2000-\u200D\u2028\u2029\u202F\u205F\u3000\uFEFF]/g

/**
 * Returns a normalised absolute URL, or null when the value is missing or
 * not safe to navigate to. Callers must treat null as "render no link".
 */
export const sanitizeExternalUrl = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') return null

  // Strip controls and exotic whitespace before parsing. The URL parser
  // already ignores tabs and newlines inside a scheme (so "jav\tascript:"
  // parses as javascript: and would be caught below), but removing them
  // first means the value we test is the value we return.
  const cleaned = value.replace(CONTROL_AND_SPACE, '')
  if (!cleaned) return null

  let parsed: URL
  try {
    // No base argument: relative and protocol-relative values ("//evil.com")
    // throw here rather than silently resolving against the current origin.
    parsed = new URL(cleaned)
  } catch {
    return null
  }

  if (!ALLOWED_PROTOCOLS.includes(parsed.protocol)) return null
  if (!parsed.hostname) return null

  // Embedded credentials ("https://apple.com@evil.com") are a phishing
  // pattern that reads as a trusted host at a glance. No legitimate tenant
  // website link needs them.
  if (parsed.username || parsed.password) return null

  return parsed.toString()
}

/** True when the value is safe to navigate to. */
export const isSafeExternalUrl = (value: string | null | undefined): boolean =>
  sanitizeExternalUrl(value) !== null
